import { FC } from 'react';
import { css } from '@emotion/react';
import {
  DigitalFormsFormDoesNotExistSvg,
  DigitalFormsFormExpiredSvg,
  DigitalFormsFormReceivedSvg,
  DigitalFormsFormUnableToFetchSvg,
} from '@frontend/digital-forms-assets';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, emptyStateGraphics, Heading, Text } from '@frontend/design-system';
import { useGetLocationDetails } from '../../../hooks/api-hooks/use-get-location-details';
import ContentContainer from '../../layout/content-container.component';
import Footer from '../../layout/footer.component';
import Header from '../../layout/header.component';

const TaskApiErrorSvg = emptyStateGraphics.widgetTaskApiError;

export type FormNotFoundVariant =
  | 'not-found'
  | 'expired'
  | 'already-submitted'
  | 'error'
  | 'empty-form'
  | 'link-broken';

interface FormNotFoundProps {
  variant: FormNotFoundVariant;
  /** Shown as a retry action when `variant` is `error`. */
  onRetry?: () => void;
  /** Optional locationId forwarded to Header so it can fetch the logo/name
   *  without relying on FillingExperienceContext (which is not mounted for error/fallback pages). */
  locationId?: string;
  /** Logo from an already-fetched form template; skips the Header logo fetch when provided. */
  logoUrl?: string;
}

const pageStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
});

const cardStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  width: '100%',
  minHeight: 500,
  padding: theme.spacing(3, 2),
  boxSizing: 'border-box',
  textAlign: 'center',
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.floating,
});

const imageStyle = css({
  width: '100%',
  minWidth: 150,
  maxWidth: 'min(300px, 40vw)',
  height: 'auto',
});

/** Shared placeholder for the not-found/expired/already-submitted/error states across all filling-experience flows. */
export const FormNotFound: FC<FormNotFoundProps> = ({ variant, onRetry, locationId, logoUrl }) => {
  const { t } = useTranslation('digital-forms-experience');
  const { locationDetails } = useGetLocationDetails(locationId ?? '');
  const officeName = locationDetails?.location_nickname || locationDetails?.name || t('us');

  const configByVariant: Record<FormNotFoundVariant, { image: JSX.Element; title: string; body: string }> = {
    'not-found': {
      image: <DigitalFormsFormDoesNotExistSvg css={imageStyle} aria-label={t('form does not exist image')} />,
      title: t('This form no longer exists.'),
      body: t('If you need assistance, please reach out to {{officeName}}.', { officeName }),
    },
    expired: {
      image: <DigitalFormsFormExpiredSvg css={imageStyle} aria-label={t('form expired image')} />,
      title: t('This form is no longer available.'),
      body: t(
        'This form link is not valid anymore. If you believe this is an error, please get in touch with our office.'
      ),
    },
    'already-submitted': {
      image: <DigitalFormsFormReceivedSvg css={imageStyle} aria-label={t('form received image')} />,
      title: t("Your form's trip is complete."),
      body: t('Thank you.'),
    },
    error: {
      image: <DigitalFormsFormUnableToFetchSvg css={imageStyle} aria-label={t('unable to fetch form image')} />,
      title: t('Something went wrong'),
      body: t('We were unable to load this form. Please check your connection and try again.'),
    },
    'empty-form': {
      image: <DigitalFormsFormUnableToFetchSvg css={imageStyle} aria-label={t('unable to fetch form image')} />,
      title: t('Unable to Access form'),
      body: t(
        'This form includes a payment section and cannot be accessed via public links. Please contact {{officeName}} to request a unique link.',
        { officeName }
      ),
    },
    'link-broken': {
      image: <TaskApiErrorSvg css={imageStyle} aria-label={t('broken link image')} data-testid='task-api-error-svg' />,
      title: t('This link is broken.'),
      body: t(
        'The link you followed is missing some information. Please check the link or contact {{officeName}} for a new one.',
        {
          officeName,
        }
      ),
    },
  };

  const { image, title, body } = configByVariant[variant];

  return (
    <>
      <Header
        showCountrySelector={false}
        locationId={locationId ?? ''}
        logoUrl={logoUrl}
        officeName={locationDetails?.location_nickname || locationDetails?.name || ''}
      />
      <ContentContainer contentCss={pageStyle}>
        <div css={cardStyle}>
          {image}
          <Heading level={2}>{title}</Heading>
          <Text css={{ maxWidth: '400px' }}>{body}</Text>
          {variant === 'error' && onRetry && <Button onClick={onRetry}>{t('Try again')}</Button>}
        </div>
      </ContentContainer>
      <Footer locationId={locationId ?? ''} />
    </>
  );
};
